<template>
    <v-form ref="form">
        <div>
            <fe-label>Primary Interventionist</fe-label>
            <fe-remote-combo
                itemText="user_full_name"
                itemValue="id"
                :items="interventionists"
                :rules="$fieldValidators('select', 'Interventionist', { required: true })"
                v-model="localValue.user_id"
                data-test="create-intervention-details-primary-interventionist"
            />
        </div>
        <a
            v-if="!showAdditionalInterventionsts"
            @click="showAdditionalInterventionsts = true"
            style="position:relative;left:20px;top:-15px;"
            data-test="create-intervention-details-add-additional-interventionists-show"
        >
            <v-icon size="12" color="#006c90" style="position:relative;top:-2px;padding-right:10px;">fas fa-plus</v-icon> Add Interventionists
        </a>

        <div v-if="showAdditionalInterventionsts">
            <fe-label>
                Additional Interventionists (Optional)
                <fe-info-tip
                    class="ml-2"
                    style="display:inline-block;"
                    tooltip="You can add up to 5 additional interventionists."
                    direction="bottom"
                    size="14px"
                    color="#495B8F"
                />
            </fe-label>
            <fe-remote-combo
                v-model="localValue.secondary_interventionists"
                itemText="user_full_name"
                itemValue="id"
                chips multiple
                :clearable="false"
                :items="filteredSecondaryInterventionists"
                :selectAllOption="false"
                :rules="[v => v.length <= 5 || 'You can add up to 5 secondary interventionists.']"
                data-test="create-intervention-details-additional-interventionists"
            />
        </div>

        <div>
            <fe-label>Intervention Level</fe-label>
            <fe-remote-combo
                :url="$models.getUrl('interventionLevel', 'read') + '&active_only=1'"
                rootProperty="intervention_levels"
                :rules="$fieldValidators('select', 'Intervention level', { required: true })"
                v-model="localValue.intervention_level_id"
                data-test="create-intervention-details-intervention-level"
            />
        </div>

        <div>
            <fe-label>Skill Area</fe-label>
            <fe-remote-combo
                :items="categories"
                :rules="$fieldValidators('select', 'Skill area', { required: true })"
                v-model="localValue.category_rec"
                @change="changeSkillArea"
                :disabled="editBehaviorPlan?true:false"
                data-test="create-intervention-details-skill-area"
            />
        </div>

        <div>
            <fe-label>Strategy</fe-label>
            <fe-remote-combo
                :disabled="!localValue.category_rec"
                :items="interventionTypes"
                :rules="$fieldValidators('select', 'Intervention strategy', { required: true, disabled: !localValue.category_rec })"
                v-model="localValue.intervention_type_id"
                @change="changeStrategy"
                data-test="create-intervention-details-strategy"
            />
        </div>

        <div>
            <fe-label class="d-flex align-center">
                Display Name
                <fe-tooltip :tooltip="`The display name of the intervention that will appear in reporting.${fastbridgeMonitor && fastbridgeMonitor.intervention_name ? ' The name can be changed here for display in eduCLIMBER (but the change will not be visible in FastBridge).' : ''}`">
                    <v-icon class="ml-2" style="font-size: 14px" color="#495B8F">fal fa-info-circle</v-icon>
                </fe-tooltip>
            </fe-label>
            <v-text-field
                :rules="$fieldValidators('text', 'Intervention name', { required: true, limit: 100 })"
                flat solo dense
                v-model="localValue.intervention_name"
                data-test="create-intervention-details-display-name"
            />
            <div v-if="autoSuggestedName && autoSuggestedName == localValue.intervention_name" style="font-size: 12px; top: -20px; position: relative;" data-form="auto-suggested-message">
                This name is auto-generated.
            </div>
        </div>

        <div class="d-flex card-bottom">
            <fe-btn usage="secondary" @click="$emit('go','back')" data-test="intervention-details-back">Back</fe-btn>
            <fe-btn @click="nextStep" data-test="intervention-details-step-add">Continue</fe-btn>
        </div>
    </v-form>
</template>

<script>
    import { mapState } from 'vuex'

    export default {
        name: 'InterventionDetails',
        props: {
            value: {
                default() { return {} }
            },
            allowBehavior: {
                type: Boolean,
                default: true
            },
            interventionists: {
                type: Array,
                default: []
            },
            fastbridgeMonitor: {
                type: Object,
                default: () => {}
            },
            students: {
                type: Array,
                default: () => []
            },
        },
        data() {
            return {
                showAdditionalInterventionsts: false,
                localValue: {
                    user_id: null,
                    category_id: null,
                    category_rec: null,
                    secondary_interventionists: [],
                    intervention_level_id: null,
                    intervention_name: '',
                    intervention_type_id: null,
                },
                interventionTypes: [],
                categories: [],
            }
        },
        computed: {
            ...mapState('global', ['sessionUser']),
            ...mapState('intervention', ['editBehaviorPlan', 'schoolYear']),
            filteredSecondaryInterventionists() {
                // don't show the primary interventionist
                const primaryInterventionist = this.localValue.user_id
                return this.interventionists.filter(secondaryInterventionist => secondaryInterventionist.id != primaryInterventionist?.id)
            },
            autoSuggestedName() {
                if (!this.fastbridgeMonitor?.intervention_name && this.localValue.intervention_type_id && this.localValue.category_rec) {
                    return this.localValue.category_rec.name + ": " + this.localValue.intervention_type_id.name
                }
                return ''
            },
            showByParticipation() {
                return this.$store.getters['flags/flags']['ec-interventions-by-participation'] === true
            },
        },
        watch: {
            'localValue.user_id'(primaryInterventionist) {
                // remove selected primary interventionist from secondary interventionists if present
                this.localValue.secondary_interventionists = this.localValue.secondary_interventionists.filter(
                    secondaryInterventionist => secondaryInterventionist.id != primaryInterventionist?.id
                )
            },
            localValue: {
                deep: true,
                handler(v) {
                    // emit value changes to index
                    this.$emit('input', v)
                }
            },
        },
        created() {
            // pull previous values into localValue
            if (this.value) {
                this.localValue = this.$_.assign(this.localValue, this.value)
            }

            // default to current user if a user is not selected
            if (!this.localValue.user_id) {
                this.localValue.user_id = this.sessionUser.user
                this.localValue.secondary_interventionists = this.localValue.secondary_interventionists.filter(u => u.id != this.sessionUser.user.id)
            }

            // show additional interventionists if any are selected
            this.showAdditionalInterventionsts = this.localValue.secondary_interventionists.length > 0

            // load categories
            this.$modelGet('category', {exclude_attendance: this.showByParticipation ? 0 : 1}).then(r => {
                if (this.allowBehavior) {
                    this.categories = r.map(r => {
                        r.name += (r.id === 4 ? ' (Point Sheet)' : '')
                        return r
                    })
                } else {
                    this.categories = r.filter(c => c.id !== 4)
                }
                this.categories = this.categories.sort((a,b) => a.rank - b.rank)

                // If FB monitor, set assessment category (skill area)
                if (this.fastbridgeMonitor?.category_id) {
                    let fbSkill = r.filter(c => c.id === this.fastbridgeMonitor?.category_id)
                    this.localValue.category_rec = fbSkill[0]
                    this.localValue.category_id = this.fastbridgeMonitor?.category_id
                }
                this.loadTypes()
            })

            if (this.fastbridgeMonitor?.intervention_name) {
                this.localValue.intervention_name = this.fastbridgeMonitor?.intervention_name
            }
        },
        methods: {
            changeStrategy() {
                this.$store.commit('intervention/set', {
                    key: 'interventionTypeId',
                    value: this.localValue.intervention_type_id?.id
                })
                if (!this.fastbridgeMonitor?.intervention_name && this.localValue.intervention_type_id) {
                    this.autoSuggestName()
                }
            },
            changeSkillArea() {
                this.$store.commit('intervention/set', {
                    key: 'categoryId',
                    value: this.localValue.category_rec?.id ||null,
                })
                this.localValue.intervention_type_id = null
                this.localValue.intervention_name = ''
                this.localValue.category_id = this.localValue.category_rec?.id
                this.loadTypes()
            },
            loadTypes() {
                this.interventionTypes = []
                // Attendance is category_rec.id === 0
                if (this.localValue.category_rec?.id !== null && this.localValue.category_rec?.id !== undefined) {
                    this.$modelGet('interventionType', { active: 1, category_id: this.localValue.category_rec.id, student_id: this.students.map(student => student.student_id), school_year_id: this.schoolYear?.id })
                        .then(resp => {
                            this.interventionTypes = resp.filter(rec => rec.name != "")
                        })
                }
            },
            autoSuggestName() {
                if (this.autoSuggestedName) {
                    this.localValue.intervention_name = this.autoSuggestedName
                }
            },
            nextStep() {
                if (this.$refs.form.validate()) {
                    this.$emit('go')
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
</style>
